import { createContext, useContext, useEffect, useState } from "react";

const ThemeContext = createContext(null);

/**
 * Theme is fully automatic — it follows the visitor's OS / device preference
 * via the `prefers-color-scheme` media query. No manual toggle, no
 * localStorage. The resolved value is applied to <html data-theme="..."> so
 * the CSS variable system in index.css picks it up everywhere.
 */
function readSystemTheme() {
  if (typeof window === "undefined") return "dark";
  if (window.matchMedia && window.matchMedia("(prefers-color-scheme: light)").matches) {
    return "light";
  }
  return "dark";
}

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(readSystemTheme);

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
  }, [theme]);

  // Live-sync with OS preference changes (user flips system theme at runtime).
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mq = window.matchMedia("(prefers-color-scheme: light)");
    const onChange = (e) => setTheme(e.matches ? "light" : "dark");
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, isLight: theme === "light", isDark: theme === "dark" }}>
      {children}
    </ThemeContext.Provider>
  );
}

export default function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    return { theme: "dark", isLight: false, isDark: true };
  }
  return ctx;
}
